import React, { useMemo, useState } from 'react';
import { H } from './quantum/gates';
import { zeroState, basisState, applyGate, probabilities, bitstring } from './quantum/QuantumState';
import { runGrover } from './quantum/grover';
import { qft } from './quantum/qft';
import QSphere from './QSphere.jsx';

const N_QUBITS = 3;
const N_ITEMS = 8;

function GroverTab() {
  const [marked, setMarked] = useState(5);
  const [iterations, setIterations] = useState(0);

  const uniform = useMemo(
    () => applyGate(applyGate(applyGate(zeroState(N_QUBITS), H, 0, N_QUBITS), H, 1, N_QUBITS), H, 2, N_QUBITS),
    []
  );
  const history = useMemo(() => runGrover(uniform, marked, 3), [uniform, marked]);
  const probs = probabilities(history[iterations]);
  const maxProb = Math.max(...probs);

  const optimalNote = iterations === 2 ? "close to the optimal iteration count for N=8" : (iterations === 3 ? 'past the optimum — probability has already started dropping ("overshoot")' : null);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Database (click an item to mark it)</h2>
        <div className="grid grid-cols-4 gap-2 max-w-xs mx-auto">
          {Array.from({ length: N_ITEMS }, (_, i) => (
            <button
              key={i}
              onClick={() => { setMarked(i); setIterations(0); }}
              className={`aspect-square rounded-lg font-bold text-lg transition-all ${marked === i ? 'bg-purple-600 text-white shadow-md scale-105' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-purple-300'}`}
            >
              {i}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 text-center mt-2">Target: item <strong className="text-purple-700">{marked}</strong> (bitstring |{bitstring(marked, N_QUBITS)}⟩)</p>
      </div>

      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide text-center">Iterations (oracle + amplitude amplification)</h2>
        <div className="flex justify-center gap-2 mb-4">
          {[0, 1, 2, 3].map((k) => (
            <button
              key={k}
              onClick={() => setIterations(k)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm ${iterations === k ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300'}`}
            >
              {k === 0 ? 'Initial' : `Iteration ${k}`}
            </button>
          ))}
        </div>

        <div className="space-y-1.5 max-w-md mx-auto">
          {probs.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className={`font-mono w-8 ${i === marked ? 'font-bold text-purple-700' : 'text-gray-500'}`}>{i}</span>
              <div className="flex-1 bg-gray-100 rounded h-5 relative overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 ${i === marked ? 'bg-purple-600' : 'bg-gray-300'}`}
                  style={{ width: `${Math.max(0, p) * 100}%` }}
                />
              </div>
              <span className="w-14 text-right font-mono">{(p * 100).toFixed(1)}%</span>
            </div>
          ))}
        </div>

        <p className="text-center text-sm mt-4">
          P(marked) = <strong className={maxProb === probs[marked] ? 'text-purple-700' : 'text-gray-700'}>{(probs[marked] * 100).toFixed(1)}%</strong>
          {optimalNote && <span className="text-gray-500"> — {optimalNote}</span>}
        </p>
      </div>
    </div>
  );
}

function QFTTab() {
  const [inputMode, setInputMode] = useState('basis'); // 'basis' | 'uniform'
  const [basisIndex, setBasisIndex] = useState(0);

  const inputState = useMemo(() => {
    if (inputMode === 'uniform') {
      return applyGate(applyGate(applyGate(zeroState(N_QUBITS), H, 0, N_QUBITS), H, 1, N_QUBITS), H, 2, N_QUBITS);
    }
    return basisState(N_QUBITS, basisIndex);
  }, [inputMode, basisIndex]);

  const outputState = useMemo(() => qft(inputState), [inputState]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Input state</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setInputMode('uniform')}
            className={`px-4 py-2 rounded-lg font-semibold text-sm ${inputMode === 'uniform' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}
          >
            Uniform superposition
          </button>
          {Array.from({ length: N_ITEMS }, (_, i) => (
            <button
              key={i}
              onClick={() => { setInputMode('basis'); setBasisIndex(i); }}
              className={`w-10 h-10 rounded-lg font-bold text-sm ${inputMode === 'basis' && basisIndex === i ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide text-center">Before QFT</h3>
          <QSphere state={inputState} numQubits={N_QUBITS} size={240} />
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide text-center">After QFT</h3>
          <QSphere state={outputState} numQubits={N_QUBITS} size={240} />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
        Try "Uniform superposition" as input — the QFT concentrates all the amplitude onto |000⟩, the exact reverse of what H⊗H⊗H did to |000⟩ in the first place. Try a single basis
        state |j⟩ — the QFT spreads it across every basis state with a phase that winds faster the larger j is (click through the states and watch each dot's color/phase change).
      </div>
    </div>
  );
}

export default function Algorithms() {
  const [tab, setTab] = useState('grover');

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Quantum Algorithms</h1>
          <p className="text-gray-600">How quantum computers search and transform information differently from classical ones.</p>
        </div>

        <div className="flex justify-center gap-3 mb-6">
          {[
            { id: 'grover', label: "🔍 Grover's Search" },
            { id: 'qft', label: '🌀 Quantum Fourier Transform' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === t.id ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'grover' ? <GroverTab /> : <QFTTab />}
      </div>
    </div>
  );
}
